import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ExternalLink, Settings, Users } from 'lucide-react'
import { EditSubscriptionDialog } from '@/components/EditSubscriptionDialog'
import { useSubscriptions } from '@/hooks/useSubscription'
import { EmptySubscriptionListState } from '@/components/EmptySubscriptionListState'
import { BuySubscription } from '@/components/BuySubscription'
import { clsx } from 'clsx'
import { subscriptionStatus } from '@/constants/constants.ts'
import { PaymentMeta } from '@/constants/PaymentMeta.ts'
import { trpc } from '@/trpcReact'

export type SubscriptionListProps = {
  subscriptionsQuery: ReturnType<typeof useSubscriptions>
}
export const SubscriptionList = ({ subscriptionsQuery }: SubscriptionListProps) => {
  const [editStates, setEditStates] = React.useState<Record<string, boolean>>({})

  const handleOpenEdit = (id: string) => {
    setEditStates((prev) => ({ ...prev, [id]: true }))
  }

  const handleCloseEdit = (id: string) => {
    setEditStates((prev) => ({ ...prev, [id]: false }))
  }

  const canUpgradeSubscriptions = subscriptionsQuery.data?.some(
    (subscription) => subscription.seats < PaymentMeta.team.seats,
  )
  const { data: portalUrl, isLoading: isLoadingPortalUrl } = trpc.getCustomerPortalUrl.useQuery(
    {},
    { enabled: canUpgradeSubscriptions },
  )

  if (!subscriptionsQuery.data?.length) {
    return <EmptySubscriptionListState />
  }

  const numSubscriptions = subscriptionsQuery.data.length

  return (
    <>
      {subscriptionsQuery.data.map((subscription, idx) => {
        const subscriptionLabel =
          subscription.status === subscriptionStatus.revoked ? 'expired' : subscription.status

        const canUpgrade = subscription.seats < PaymentMeta.team.seats

        return (
          <Card key={subscription.id} className="bg-card">
            <CardHeader className="pb-3">
              <div
                className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2
"
              >
                <CardTitle className="text-lg">Subscription #{numSubscriptions - idx}</CardTitle>
                <Button
                  variant="outline"
                  type={'button'}
                  onClick={() => handleOpenEdit(subscription.id)}
                  disabled={subscription.status === subscriptionStatus.revoked}
                >
                  <Settings className="h-4 w-4" />
                  Manage this subscription{' '}
                  {subscription.status === subscriptionStatus.revoked && '(no access)'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Seats</span>
                  </div>
                  <span className="text-sm">{subscription.seats}</span>
                </div>
                {canUpgrade && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Upgrade license to Team</span>
                    </div>
                    <Button variant="outline" asChild disabled={isLoadingPortalUrl}>
                      <a
                        href={portalUrl?.customerPortalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        upgrade here
                      </a>
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Status</span>
                  <span
                    className={clsx(
                      'text-sm px-2 py-1 rounded-md capitalize',
                      subscription.status === 'active'
                        ? 'bg-secondary'
                        : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {subscriptionLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium">Started</span>
                  <span className="text-sm text-muted-foreground">
                    {new Date(subscription.started_at).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              </div>
            </CardContent>

            <EditSubscriptionDialog
              subscription={subscription}
              open={editStates[subscription.id] || false}
              onOpenChange={(open) =>
                open ? handleOpenEdit(subscription.id) : handleCloseEdit(subscription.id)
              }
            />
          </Card>
        )
      })}
      <BuySubscription currentSubscriptions={subscriptionsQuery.data} />
    </>
  )
}
